import { TDocumentType } from '@/@types/document-type';
import { api } from '@/services/apiClient';
import { queryClient } from '@/services/queryClient';
import { useMutation } from '@tanstack/react-query';
import { getDocumentType } from './useGetDocumentType';

interface CreateDocumentTypeProps {
  name: string;
  type: string;
  affectsCashMovement: boolean;
}

interface UpdateDocumentTypeProps {
  id: string;
  name: string;
  type: string;
  affectsCashMovement: boolean;
}

interface DeleteDocumentTypeProps {
  id: string;
}

interface RestoreDocumentTypeProps {
  id: string;
}

type CreateDocumentTypeResponse = TDocumentType;
type UpdateDocumentTypeResponse = TDocumentType;
type RestoreDocumentTypeResponse = TDocumentType;

async function createDocumentType({
  name,
  type,
  affectsCashMovement,
}: CreateDocumentTypeProps): Promise<CreateDocumentTypeResponse> {
  const { data } = await api.post('/document_types', {
    name,
    type,
    affects_cash_movement: affectsCashMovement,
  });

  return data;
}

async function updateDocumentType({
  id,
  name,
  type,
  affectsCashMovement,
}: UpdateDocumentTypeProps): Promise<UpdateDocumentTypeResponse> {
  const { data } = await api.put('/document_types', {
    id,
    name,
    type,
    affects_cash_movement: affectsCashMovement,
  });

  return data;
}

async function deleteDocumentType({
  id,
}: DeleteDocumentTypeProps): Promise<void> {
  await api.delete(`/document_types/${id}`);
}

async function restoreDocumentType({
  id,
}: RestoreDocumentTypeProps): Promise<RestoreDocumentTypeResponse> {
  const { data } = await api.patch(`/document_types/${id}`);

  return data;
}

export function useDocumentType() {
  const createDocumentTypeMutation = useMutation({
    mutationFn: ({
      name,
      type,
      affectsCashMovement,
    }: CreateDocumentTypeProps) =>
      createDocumentType({ name, type, affectsCashMovement }),
    onSuccess: () => {
      queryClient.invalidateQueries(['documentTypes']);
    },
  });

  const updateDocumentTypeMutation = useMutation({
    mutationFn: ({
      id,
      name,
      type,
      affectsCashMovement,
    }: UpdateDocumentTypeProps) =>
      updateDocumentType({ id, name, type, affectsCashMovement }),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['documentTypes']);
      queryClient.invalidateQueries(['documentTypes', data.id]);
    },
  });

  const deleteDocumentTypeMutation = useMutation({
    mutationFn: ({ id }: DeleteDocumentTypeProps) => deleteDocumentType({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries(['documentTypes']);
    },
  });

  const restoreDocumentTypeMutation = useMutation({
    mutationFn: ({ id }: RestoreDocumentTypeProps) =>
      restoreDocumentType({ id }),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['documentTypes']);
      queryClient.invalidateQueries(['documentTypes', data.id]);
    },
  });

  async function prefetchDocumentType(documentTypeId: string) {
    await queryClient.prefetchQuery(
      ['documentTypes', documentTypeId],
      () => getDocumentType({ id: documentTypeId }),
      {
        staleTime: 1000 * 60 * 1, // 1 minute
      }
    );
  }

  return {
    createDocumentTypeMutation,
    updateDocumentTypeMutation,
    deleteDocumentTypeMutation,
    restoreDocumentTypeMutation,
    prefetchDocumentType,
  };
}
